import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageTitle from "../../components/generics/page-title";
import reviewsViewModel from '../../view-model/professional/reviews-view-model';
import ReviewStats from '../../components/reviews/review-stats';
import ReviewList from '../../components/reviews/review-list';
import { RefreshCw, Lock } from 'lucide-react';
import subscriptionStore from '../../store/subscription-store';

const Reviews: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language || 'fr';
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Vérifier si l'utilisateur a accès à cette fonctionnalité
  const hasReviewSystem = subscriptionStore.currentSubscription?.reviewSystem ?? false;

  const loadData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        reviewsViewModel.fetchMyReviews(),
        reviewsViewModel.fetchMyStats()
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      await Promise.all([
        reviewsViewModel.fetchMyReviews(),
        reviewsViewModel.fetchMyStats()
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (hasReviewSystem) {
      loadData();
    }
  }, [hasReviewSystem]);

  // Si l'utilisateur n'a pas accès à cette fonctionnalité, afficher un message de blocage
  if (!hasReviewSystem) {
    return (
      <div className="h-[calc(100vh-2rem)] flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-amber-600 dark:text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {t('subscription.featureLocked', 'Fonctionnalité Premium')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('subscription.reviewsLockedMessage', 'Le système d\'avis nécessite un abonnement Premium ou Premium+.')}
          </p>
          <button
            onClick={() => navigate(`/${currentLang}/provider/subscription`)}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            {t('subscription.upgrade', 'Mettre à niveau')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-2rem)] flex flex-col space-y-3">
      <PageTitle 
        title={t('reviews.pageTitle')}
        actions={
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50"
          >
            <RefreshCw size={16} className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? t('reviews.refreshing') : t('reviews.refresh')}
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne de gauche: Statistiques */}
        <div className="lg:col-span-1">
          <ReviewStats isLoading={isLoading} />
        </div>

        {/* Colonne de droite: Liste des avis */}
        <div className="lg:col-span-2">
          <ReviewList isLoading={isLoading} />
        </div>
      </div>
    </div>
  );
});

export default Reviews;